import {
  batchCreateFiles,
  batchCreateFolders,
  createFolder,
  updateFileContent,
} from '../drive'
import PQueue from 'p-queue'
import { FileData, FolderData } from './types'

const boundaryRegex = /--batch_.+/g // Matches boundary lines

// Function to parse the batch response and extract folder IDs with their corresponding folder names
function createFolderIdMapFromBatchCreateFoldersResponse(
  batchResponse: string,
  folders: FolderData[],
): { [key: string]: FolderData } {
  const parts = batchResponse
    .split(boundaryRegex)
    .filter((part) => part.trim() !== '')

  return folders.reduce(
    (map, folder, index) => {
      const expectedContentId = `<response-${index}>`

      // Find the part with the corresponding Content-ID
      const matchingPart = parts.find((part) => {
        return part.includes(`Content-ID: ${expectedContentId}`)
      })

      if (!matchingPart) {
        throw new Error(
          `No matching response found for folder: ${folder.name} with parent ID: ${folder.parentId}`,
        )
      }

      const jsonStart = matchingPart.indexOf('{')
      const jsonEnd = matchingPart.lastIndexOf('}')
      if (jsonStart === -1 || jsonEnd === -1) {
        throw new Error('Invalid JSON response in batch request')
      }

      const jsonResponse = matchingPart.substring(jsonStart, jsonEnd + 1)
      const parsedData = JSON.parse(jsonResponse)
      const folderId = parsedData.id

      // Map the file ID to the corresponding FileData object
      if (folderId) {
        map[folderId] = folder
      }

      return map
    },
    {} as Record<string, FolderData>,
  )
}

function createFileIdMapFromBatchCreateFilesResponse(
  batchResponse: string,
  files: FileData[],
): { [fileId: string]: FileData } {
  const parts = batchResponse
    .split(boundaryRegex)
    .filter((part) => part.trim() !== '')

  return files.reduce(
    (map, file, index) => {
      const expectedContentId = `<response-${index}>`

      // Find the part with the corresponding Content-ID
      const matchingPart = parts.find((part) => {
        return part.includes(`Content-ID: ${expectedContentId}`)
      })

      if (!matchingPart) {
        throw new Error(
          `No matching response found for file: ${file.name} with parent ID: ${file.parentId}`,
        )
      }

      const jsonStart = matchingPart.indexOf('{')
      const jsonEnd = matchingPart.lastIndexOf('}')
      if (jsonStart === -1 || jsonEnd === -1) {
        throw new Error('Invalid JSON response in batch request')
      }

      const jsonResponse = matchingPart.substring(jsonStart, jsonEnd + 1)
      const parsedData = JSON.parse(jsonResponse)
      const fileId = parsedData.id

      // Map the file ID to the corresponding FileData object
      if (fileId) {
        map[fileId] = file
      }

      return map
    },
    {} as Record<string, FileData>,
  )
}

function calculateTotalItems(folderTree: FolderData): number {
  let totalItems = 1 // Count the current folder

  // Count subfolders recursively
  if (folderTree.subfolders && folderTree.subfolders.length > 0) {
    folderTree.subfolders.forEach((subfolder) => {
      totalItems += calculateTotalItems(subfolder)
    })
  }

  // Count files in the current folder
  if (folderTree.files && folderTree.files.length > 0) {
    totalItems += folderTree.files.length
  }

  return totalItems
}

export class FileStructureProcessor {
  folderData: FolderData
  accessToken: string
  queue = new PQueue({ concurrency: 250 })
  progress = { total: 0, completed: 0 }
  allFilesWithParents: FileData[] = []
  fileIdMap: { [key: string]: FileData } = {}

  private refreshProgress: () => void

  constructor(
    folderData: FolderData,
    accessToken: string,
    setProgress?: (
      progress: number,
      total?: number,
      completed?: number,
    ) => void,
  ) {
    this.folderData = folderData
    this.accessToken = accessToken
    this.progress.total = calculateTotalItems(folderData)
    this.refreshProgress = () => {
      setProgress?.(
        this.progress.completed / this.progress.total,
        this.progress.total,
        this.progress.completed,
      )
    }
  }

  private addToQueue<T>(fn: () => Promise<T>): Promise<T> {
    // Add the function to the queue and return the result of the promise
    return this.queue.add(fn) as Promise<T>
  }

  private addToCompleted(completed: number) {
    this.progress.completed += completed
    this.refreshProgress()
  }

  private async createFolderAndSubfolders(
    folderTree: FolderData,
    parentId: string | null = null,
  ): Promise<string> {
    // Step 1: Create the current folder if parentId is null
    const currentFolderId = parentId
      ? parentId
      : await this.addToQueue(() =>
          createFolder(this.accessToken, folderTree.name),
        )

    // Step 2: Create subfolders in batch if there are any
    if (folderTree.subfolders && folderTree.subfolders.length > 0) {
      try {
        const response = await this.addToQueue(() =>
          batchCreateFolders(
            this.accessToken,
            folderTree.subfolders!,
            currentFolderId!,
          ),
        )
        const subfolderIdMap = createFolderIdMapFromBatchCreateFoldersResponse(
          response,
          folderTree.subfolders!,
        )
        const subfolderPromises = Object.entries(subfolderIdMap).map(
          async ([id, folderData]) => {
            await this.createFolderAndSubfolders(folderData, id)
          },
        )

        // Wait for all recursive calls to complete
        await Promise.all(subfolderPromises)
      } finally {
        this.addToCompleted(folderTree.subfolders.length)
      }
    }

    // Step 3: Save files that need to be created with correct parent ID so we can batch create later
    if (folderTree.files && folderTree.files.length > 0) {
      this.allFilesWithParents.push(
        ...folderTree.files.map((file) => ({
          ...file,
          parentId: currentFolderId,
        })),
      )
    }

    return currentFolderId
  }

  private async createFiles() {
    const createFolderPromises: Promise<void | string>[] = []
    const createFilePromises: Promise<string>[] = []

    for (let i = 0; i < this.allFilesWithParents.length; i += 50) {
      const files = this.allFilesWithParents.slice(i, i + 50)

      // Don't await here, so the next loop iteration can start while this one is processing
      const createPromise = this.addToQueue(() =>
        batchCreateFiles(this.accessToken, files),
      ).then((response) => {
        const fileIdMap = createFileIdMapFromBatchCreateFilesResponse(
          response,
          files,
        )

        // Process file content updates concurrently
        const updatePromises = Object.entries(fileIdMap).map(
          ([id, fileData]) => {
            return this.addToQueue(() =>
              updateFileContent(this.accessToken, id, fileData),
            ).finally(() => {
              this.addToCompleted(1)
            })
          },
        )

        createFilePromises.push(...updatePromises)
      })

      // Collect the create promise into the update promises
      createFolderPromises.push(createPromise)
    }

    // Wait for all file creation and content updates to complete
    await Promise.all(createFolderPromises)
    return Promise.all(createFilePromises)
  }

  async createFoldersAndFiles(): Promise<string> {
    const folderId = await this.createFolderAndSubfolders(this.folderData)
    await this.createFiles()
    return folderId
  }
}
